/**
 * PriceBreakdown Component
 *
 * MVP 1.22 - Experience Pricing & Promotions Engine
 * Story: SCRUM-373 - Understand Price Breakdown
 *
 * Detailed price breakdown with expandable sections.
 *
 * Acceptance Criteria:
 * - AC-002.1: Each line item has clear label and amount
 * - AC-002.2: Premiums show percentage AND dollar amount
 * - AC-002.3: Discounts highlighted in green with savings
 * - AC-002.4: Platform fee separated with tooltip explanation
 * - AC-002.5: Expandable "View full breakdown" reveals details
 * - AC-002.6: Grand total prominently displayed
 * - AC-002.7: All modifiers show percentage AND dollar in expanded view
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { formatCentsToDollars } from '../../utils/formHelpers';

/**
 * Individual line item in the breakdown
 */
const LineItem = ({ label, amount, isDiscount, isSubtotal, isTotal, indent }) => {
  const textColor = isDiscount ? 'text-green-600' : 'text-gray-700';
  const bgColor = isDiscount ? 'bg-green-50' : '';

  let fontWeight = 'font-normal text-sm';
  if (isSubtotal) {fontWeight = 'font-medium text-sm';}
  if (isTotal) {fontWeight = 'font-bold text-lg';}

  return (
    <div
      className={`flex justify-between items-center py-1.5 ${bgColor} ${indent ? 'pl-4' : ''} ${isTotal ? 'pt-3 border-t-2 border-gray-300' : ''}`}
    >
      <span className={`${fontWeight} ${isDiscount ? 'text-green-700' : 'text-gray-600'}`}>
        {label}
      </span>
      <span className={`${fontWeight} ${textColor}`}>
        {isDiscount && amount > 0 ? '-' : ''}{formatCentsToDollars(Math.abs(amount))}
      </span>
    </div>
  );
};

LineItem.propTypes = {
  label: PropTypes.string.isRequired,
  amount: PropTypes.number.isRequired,
  isDiscount: PropTypes.bool,
  isSubtotal: PropTypes.bool,
  isTotal: PropTypes.bool,
  indent: PropTypes.bool
};

/**
 * Modifier line showing both percentage and dollar amount (AC-002.2, AC-002.7)
 */
const ModifierLine = ({ modifier }) => {
  const isDiscount = modifier.amountCents < 0;

  return (
    <div className={`flex justify-between items-center py-1.5 ${isDiscount ? 'bg-green-50' : ''}`}>
      <span className={`text-sm ${isDiscount ? 'text-green-700' : 'text-gray-600'}`}>
        {modifier.label}
        {modifier.percentage && (
          <span className={`ml-2 text-xs px-1.5 py-0.5 rounded ${isDiscount ? 'bg-green-200 text-green-800' : 'bg-amber-100 text-amber-800'}`}>
            {modifier.percentage > 0 ? '+' : ''}{modifier.percentage}%
          </span>
        )}
      </span>
      <span className={`text-sm font-medium ${isDiscount ? 'text-green-600' : 'text-gray-700'}`}>
        {modifier.amountCents > 0 ? '+' : ''}{formatCentsToDollars(modifier.amountCents)}
      </span>
    </div>
  );
};

ModifierLine.propTypes = {
  modifier: PropTypes.shape({
    label: PropTypes.string.isRequired,
    amountCents: PropTypes.number.isRequired,
    percentage: PropTypes.number
  }).isRequired
};

/**
 * Platform fee line with info tooltip (AC-002.4)
 */
const PlatformFeeLine = ({ feeCents, feePercent }) => (
  <div className="flex justify-between items-center py-1.5">
    <span className="text-sm text-gray-600 flex items-center gap-1">
      Platform fee ({feePercent}%)
      <span className="group relative">
        <svg className="w-4 h-4 text-gray-400 cursor-help" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z"
            clipRule="evenodd"
          />
        </svg>
        <div className="invisible group-hover:visible absolute z-10 w-64 p-2 text-xs text-gray-600 bg-white border border-gray-200 rounded-lg shadow-lg bottom-6 left-0">
          This fee supports the OutdoorGuides platform, including 24/7 customer support, secure payment processing, and trip protection services.
        </div>
      </span>
    </span>
    <span className="text-sm text-gray-700">{formatCentsToDollars(feeCents)}</span>
  </div>
);

PlatformFeeLine.propTypes = {
  feeCents: PropTypes.number.isRequired,
  feePercent: PropTypes.number.isRequired
};

/**
 * Expandable section toggle
 */
const ExpandToggle = ({ expanded, onToggle, hasDetails }) => {
  if (!hasDetails) {return null;}

  return (
    <button
      type="button"
      onClick={onToggle}
      className="text-sm text-green-600 hover:text-green-700 font-medium flex items-center gap-1 mt-2"
    >
      <svg
        className={`w-4 h-4 transform transition-transform ${expanded ? 'rotate-180' : ''}`}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
      {expanded ? 'Hide details' : 'View full breakdown'}
    </button>
  );
};

ExpandToggle.propTypes = {
  expanded: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  hasDetails: PropTypes.bool.isRequired
};

/**
 * PriceBreakdown Component
 *
 * Full detailed breakdown with expandable sections.
 */
const PriceBreakdown = ({
  pricing,
  partySize = 2,
  showExpandable = true,
  defaultExpanded = false,
  className = ''
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  if (!pricing) {
    return (
      <div className={`bg-gray-50 rounded-lg p-4 ${className}`}>
        <p className="text-gray-500 text-sm">Pricing not available</p>
      </div>
    );
  }

  const hasModifiers = pricing.modifiers && pricing.modifiers.length > 0;
  const hasAddons = pricing.addons && pricing.addons.length > 0;
  const hasPromotion = pricing.promotion;
  const hasDetails = hasModifiers || hasAddons;

  // Calculate savings if there's a promotion
  const savingsAmount = pricing.promotionDiscountCents || 0;

  return (
    <div className={`bg-white rounded-lg border border-gray-200 ${className}`}>
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-100">
        <h3 className="font-semibold text-gray-900">Price Breakdown</h3>
      </div>

      {/* Content */}
      <div className="px-4 py-3 space-y-1">
        {/* Base Price */}
        <LineItem
          label={`Base price (${partySize} ${partySize === 1 ? 'guest' : 'guests'})`}
          amount={pricing.basePriceCents}
        />

        {/* Collapsed view: Summary only */}
        {!expanded && hasDetails && (
          <div className="text-sm text-gray-500 py-1">
            {hasModifiers && `${pricing.modifiers.length} rate adjustment${pricing.modifiers.length > 1 ? 's' : ''}`}
            {hasModifiers && hasAddons && ' + '}
            {hasAddons && `${pricing.addons.length} add-on${pricing.addons.length > 1 ? 's' : ''}`}
          </div>
        )}

        {/* Expanded view: Full details (AC-002.5) */}
        {expanded && (
          <>
            {/* Modifiers (AC-002.2, AC-002.7) */}
            {hasModifiers && (
              <div className="border-t border-gray-100 pt-2 mt-2">
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Rate Adjustments</p>
                {pricing.modifiers.map((modifier, index) => (
                  <ModifierLine key={index} modifier={modifier} />
                ))}
              </div>
            )}

            {/* Add-ons */}
            {hasAddons && (
              <div className="border-t border-gray-100 pt-2 mt-2">
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Add-ons</p>
                {pricing.addons.map((addon, index) => (
                  <LineItem
                    key={index}
                    label={`${addon.name}${addon.quantity > 1 ? ` (x${addon.quantity})` : ''}`}
                    amount={addon.lineTotalCents}
                    indent
                  />
                ))}
              </div>
            )}
          </>
        )}

        {/* SCRUM-1292 AC-09: Per-participant line items when participant types applied */}
        {pricing.participantDetails && pricing.participantDetails.length > 0 && (
          <div className="border-t border-gray-100 pt-2 mt-2" data-testid="participant-breakdown">
            <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Participant Pricing</p>
            {pricing.participantDetails.map((p, i) => {
              const isDiscount = p.discountPercent > 0;
              return (
                <div
                  key={i}
                  className={`flex justify-between items-center py-1 ${isDiscount ? 'bg-green-50' : ''}`}
                  data-testid={`participant-line-${i}`}
                >
                  <span className="text-sm text-gray-600">
                    Participant {i + 1}
                    {p.age !== undefined && p.age !== null && ` (Age ${p.age})`}
                    {p.typeName && ` — ${p.typeName}`}
                  </span>
                  <span className={`text-sm font-medium ${isDiscount ? 'text-green-600' : 'text-gray-700'}`}>
                    {formatCentsToDollars(p.priceCents)}
                    {isDiscount && (
                      <span className="ml-1 text-xs text-green-600">-{p.discountPercent}%</span>
                    )}
                  </span>
                </div>
              );
            })}
          </div>
        )}

        {/* Promotion/Discount (AC-002.3 - highlighted in green) */}
        {hasPromotion && (
          <div className="bg-green-50 -mx-4 px-4 py-2 mt-2">
            <div className="flex justify-between items-center">
              <span className="text-sm text-green-700 font-medium">
                Promo: {pricing.promotion.code}
                <span className="ml-2 text-xs text-green-600">
                  You save {formatCentsToDollars(savingsAmount)}!
                </span>
              </span>
              <span className="text-sm font-medium text-green-600">
                -{formatCentsToDollars(savingsAmount)}
              </span>
            </div>
          </div>
        )}

        {/* Expand toggle */}
        {showExpandable && (
          <ExpandToggle
            expanded={expanded}
            onToggle={() => setExpanded(!expanded)}
            hasDetails={hasDetails}
          />
        )}

        {/* Subtotal (if there are modifiers/addons/promos) */}
        {(hasModifiers || hasAddons || hasPromotion) && (
          <LineItem
            label="Subtotal"
            amount={pricing.subtotalCents}
            isSubtotal
          />
        )}

        {/* Platform Fee (AC-002.4) */}
        <PlatformFeeLine
          feeCents={pricing.platformFeeCents}
          feePercent={pricing.platformFeePercent}
        />

        {/* Sales Tax — SCRUM-2057: hidden when zero */}
        {pricing.taxAmountCents > 0 && (
          <LineItem
            label="Sales tax"
            amount={pricing.taxAmountCents}
          />
        )}

        {/* Total (AC-002.6 - prominently displayed) */}
        <LineItem
          label="Total"
          amount={pricing.totalCents}
          isTotal
        />

        {/* Per-person breakdown */}
        {partySize > 1 && (
          <p className="text-xs text-gray-500 text-right pt-1">
            {formatCentsToDollars(Math.round(pricing.totalCents / partySize))} per person
          </p>
        )}
      </div>
    </div>
  );
};

PriceBreakdown.propTypes = {
  pricing: PropTypes.shape({
    basePriceCents: PropTypes.number.isRequired,
    modifiers: PropTypes.arrayOf(PropTypes.shape({
      label: PropTypes.string.isRequired,
      amountCents: PropTypes.number.isRequired,
      percentage: PropTypes.number
    })),
    addons: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string.isRequired,
      lineTotalCents: PropTypes.number.isRequired,
      quantity: PropTypes.number
    })),
    participantDetails: PropTypes.arrayOf(PropTypes.shape({
      age: PropTypes.number,
      typeName: PropTypes.string,
      priceCents: PropTypes.number.isRequired,
      discountPercent: PropTypes.number
    })),
    promotion: PropTypes.shape({
      code: PropTypes.string.isRequired,
      name: PropTypes.string
    }),
    promotionDiscountCents: PropTypes.number,
    subtotalCents: PropTypes.number.isRequired,
    platformFeeCents: PropTypes.number.isRequired,
    platformFeePercent: PropTypes.number.isRequired,
    taxAmountCents: PropTypes.number,
    totalCents: PropTypes.number.isRequired
  }),
  partySize: PropTypes.number,
  showExpandable: PropTypes.bool,
  defaultExpanded: PropTypes.bool,
  className: PropTypes.string
};

export default PriceBreakdown;
